import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Camera, Image, X } from 'lucide-react';

const GalleryPage = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load gallery data from JSON file
  useEffect(() => {
    fetch('/gallery/gallery.json')
      .then(response => response.json())
      .then(data => {
        setGalleryImages(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading gallery data:', error);
        setLoading(false);
      });
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') previousImage();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'Escape') setIsFullscreen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentImage]);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % galleryImages.length);
  };

  const previousImage = () => {
    setCurrentImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const goToImage = (index) => {
    setCurrentImage(index);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading gallery...</p>
        </div>
      </div>
    );
  }

  // Show empty state if no images
  if (galleryImages.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No gallery images available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      {/* Hero Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-6xl font-bold mb-4 text-charcoal">Gallery</h1>
          <div className="w-32 h-1 bg-olive mx-auto mb-6"></div>
          <p style={{ fontSize: '20px', color: '#4a7043', fontStyle: 'italic' }}>La Nostra Galleria</p>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mt-8">
            Take a visual journey through The Pizza House - from our authentic Italian atmosphere 
            to our warm, welcoming restaurant and rich family history in Peterborough.
          </p>
        </div>
      </section>

      {/* Main Gallery Carousel */}
      <section className="py-8" role="region" aria-label="Gallery carousel">
        <div className="max-w-7xl mx-auto px-6">
          {/* Main Image Display */}
          <div className="relative bg-white rounded-lg shadow-2xl overflow-hidden mb-8">
            <div className="relative aspect-[16/10] lg:aspect-[21/9]">
              <img
                src={galleryImages[currentImage].src}
                alt={galleryImages[currentImage].title}
                className="w-full h-full object-cover cursor-pointer"
                onClick={toggleFullscreen}
              />
              
              {/* Image Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent">
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex justify-between items-end">
                    <div className="text-white">
                      <span className="inline-block bg-primary px-3 py-1 rounded-full text-sm font-medium mb-3">
                        {galleryImages[currentImage].category}
                      </span>
                      <h2 className="text-3xl md:text-4xl font-bold mb-2">
                        {galleryImages[currentImage].title}
                      </h2>
                    </div>
                    <button
                      onClick={toggleFullscreen}
                      className="bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 transition-colors"
                    >
                      <Camera size={24} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={previousImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 backdrop-blur-sm text-white p-3 rounded-full hover:bg-black/70 transition-colors"
              >
                <ChevronLeft size={24} />
              </button>
              
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 backdrop-blur-sm text-white p-3 rounded-full hover:bg-black/70 transition-colors"
              >
                <ChevronRight size={24} />
              </button>

              {/* Image Counter */}
              <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
                {currentImage + 1} / {galleryImages.length}
              </div>
            </div>
          </div>

          {/* Thumbnail Navigation */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-charcoal mb-6 flex items-center">
              <Image className="mr-3 text-primary" size={28} />
              Browse Gallery
            </h3>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {galleryImages.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => goToImage(index)}
                  className={`relative aspect-[4/3] rounded-lg overflow-hidden transition-all duration-300 ${
                    index === currentImage
                      ? 'ring-4 ring-primary shadow-lg scale-105'
                      : 'hover:shadow-md hover:scale-102'
                  }`}
                >
                  <img
                    src={image.thumbnail}
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Overlay for current image */}
                  {index === currentImage && (
                    <div className="absolute inset-0 bg-primary/20"></div>
                  )}
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                    <span className="text-white font-medium text-sm text-center px-2">
                      {image.title}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Gallery Info */}
          <div className="mt-8 bg-olive text-white rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Capturing Our Story</h3>
            <p className="text-lg opacity-90 max-w-3xl mx-auto">
              Every image tells a part of our story - from our historic location in Cowgate 
              to the warm atmosphere that has welcomed Peterborough families since 1978. 
              Use the arrow keys or click thumbnails to navigate through our gallery.
            </p>
          </div>
        </div>
      </section>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center" role="dialog" aria-modal="true">
          <button
            onClick={toggleFullscreen}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <X size={32} />
          </button>

          <button
            onClick={previousImage}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white p-3 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <ChevronLeft size={32} />
          </button>

          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white p-3 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <ChevronRight size={32} />
          </button>

          <img
            src={galleryImages[currentImage].src}
            alt={galleryImages[currentImage].title}
            className="max-w-full max-h-full object-contain"
          />

          <div className="absolute bottom-4 left-4 right-4 text-white text-center">
            <h3 className="text-2xl font-bold mb-2">{galleryImages[currentImage].title}</h3>
            <div className="mt-2 text-sm opacity-75">
              {currentImage + 1} of {galleryImages.length} • Press ESC to close
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage; 